/**
 * Список декларацій з ПДВ (J0200126, 2026-07-30) + створення за період.
 *
 * 2026-08-22 (Норма #29): серверна пагінація/сортування через
 * InfiniteDocGrid + колонка «Організація».
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { vatDeclarations, type VatDeclaration } from '@/api/vatDeclarations';
import { dpsSubmissions } from '@/api/dpsSubmissions';
import { orgs as orgsApi } from '@/api/catalogs';
import { DocListFiltersRow } from '@/shared/components/DocListFilters';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

// Статус подання (Журнал Кабінету ДПС) — колонка списку.
const SUB_STATUS: Record<string, [string, string]> = {
  DRAFT: ['чернетка', '#64748b'],
  SIGNED: ['підписана', '#7c3aed'],
  SENT: ['відправлена', '#1d4ed8'],
  DELIVERED: ['обробляється', '#0369a1'],
  ACCEPTED: ['прийнята', '#16a34a'],
  REJECTED: ['не прийнята', '#dc2626'],
  ERROR: ['помилка', '#dc2626'],
};

const MONTHS = ['січень', 'лютий', 'березень', 'квітень', 'травень', 'червень',
  'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

const fmt0 = (v: number | undefined) =>
  (v ?? 0).toLocaleString('uk-UA');

export default function VatDeclarationsPage() {
  const navigate = useNavigate();
  const { hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('tax.invoice.manage');

  // Фільтри (2026-08-07, рішення власника): орг + період (місяць/рік).
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgListQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgListQ.data]);
  // Орг — з дефолтів шапки; місяць/рік — з глобального періоду шапки,
  // якщо він задає місяць/рік (2026-08-07, зауваження власника).
  const [orgId, setOrgId] = useOrgFilter();
  const period = useDefaultPeriod();
  const [fYear, setFYear] = useState<string>(
    () => period.dateFrom ? period.dateFrom.slice(0, 4) : '');
  const [fMonth, setFMonth] = useState<string>(
    () => period.periodMode === 'month' && period.dateFrom
      ? String(Number(period.dateFrom.slice(5, 7))) : '');
  const { datasource, total } = useInfiniteGridDatasource<VatDeclaration>(
    (limit, offset, sort) => vatDeclarations.list({
      organization_id: orgId || undefined,
      period_year: fYear ? Number(fYear) : undefined,
      period_month: fMonth ? Number(fMonth) : undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, fYear, fMonth],
  );
  // Статус подання кожної декларації — з Журналу Кабінету (map по source_id).
  const subsQ = useQuery({
    queryKey: ['vat-decl-subs'],
    queryFn: () => dpsSubmissions.list({
      source_type: 'doc.vat_declarations', limit: 500,
    }),
  });
  const subByDecl = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subsQ.data?.items ?? []) {
      if (s.source_id && !m.has(s.source_id)) m.set(s.source_id, s.status);
    }
    return m;
  }, [subsQ.data]);

  const cols = useMemo<ColDef<VatDeclaration>[]>(() => [
    {
      colId: 'period', headerName: 'Період', width: 160,
      valueGetter: (p) => p.data
        ? `${MONTHS[p.data.period_month - 1]} ${p.data.period_year}` : '',
    },
    { field: 'document_number', headerName: '№', width: 160 },
    {
      field: 'organization_id', headerName: 'Організація', width: 200,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    {
      colId: 'r18', headerName: 'До сплати (р.18)', width: 150,
      cellStyle: { textAlign: 'right' },
      valueGetter: (p) => p.data ? (p.data.values?.R180GB ?? 0) : null,
      valueFormatter: (p) => p.data ? fmt0(p.value ?? 0) : '',
    },
    {
      colId: 'r21', headerName: "Від'ємне (р.21)", width: 150,
      cellStyle: { textAlign: 'right' },
      valueGetter: (p) => p.data ? (p.data.values?.R210GB ?? 0) : null,
      valueFormatter: (p) => p.data ? fmt0(p.value ?? 0) : '',
    },
    {
      // Дані Журналу Кабінету — клієнтський map, сервер не сортує.
      colId: 'submission', headerName: 'Подання', width: 150, sortable: false,
      cellRenderer: (p: { data?: VatDeclaration }) => {
        if (!p.data) return '';
        const st = subByDecl.get(p.data.id);
        if (!st) return <span style={{ color: '#94a3b8' }}>—</span>;
        const [txt, color] = SUB_STATUS[st] ?? [st, '#334155'];
        return <span style={{ color, fontWeight: st === 'ACCEPTED' ? 600 : 400 }}>{txt}</span>;
      },
    },
    {
      field: 'created_at', headerName: 'Створено', width: 160,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
  ], [subByDecl, orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Декларації з ПДВ<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/taxation/vat-declarations/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `Немає прав у жодній з ${totalAccessibleOrgs} організацій`
            : undefined}
        >
          + Створити за період
        </button>
      </div>
      <DocListFiltersRow>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 200, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <select value={fMonth} onChange={(e) => setFMonth(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}>
          <option value="">— місяць —</option>
          {['січень','лютий','березень','квітень','травень','червень','липень',
            'серпень','вересень','жовтень','листопад','грудень'].map((mn, i) => (
            <option key={i + 1} value={String(i + 1)}>{mn}</option>
          ))}
        </select>
        <select value={fYear} onChange={(e) => setFYear(e.target.value)}
          style={{ width: 110, flex: '0 0 auto' }}>
          <option value="">— рік —</option>
          {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() + 1 - i).map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<VatDeclaration>
        datasource={datasource}
        columnDefs={cols}
        storageKey="vat-declarations"
        onRowDoubleClicked={(r) => navigate(`/taxation/vat-declarations/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
